import { useState, useEffect, useRef } from "react";
import { X, Send, Plus, History } from "lucide-react";
import { ChatMessage } from "./ChatMessage";
import { useChatService } from "./useChatService";
import { useChatState } from "./chatState";

const SUGGESTED_QUESTIONS_FUNNEL = [
  "¿Cuál es mi CPA máximo rentable?",
  "¿Qué precio ideal debería probar?",
  "¿Qué costo está dañando más la utilidad?",
];

const SUGGESTED_QUESTIONS_FUNNELS = [
  "¿Qué embudo vendió más en este periodo?",
  "¿Qué embudo debería priorizar?",
  "¿Qué embudos necesitan conexión con Meta o logística?",
];

const SUGGESTED_QUESTIONS_COMPANY = [
  "¿Qué está afectando más mi utilidad neta?",
  "¿Cuál es el estado general de mi empresa?",
  "¿Dónde debo mirar primero para mejorar margen?",
];

const SUGGESTED_BY_SURFACE = {
  channels: [
    "¿Qué parte del negocio está fuera de Fluxi?",
    "¿Qué pauta está sin embudo?",
    "¿Qué canal debería revisar primero?",
  ],
  ads: [
    "¿Qué embudos debería escalar?",
    "¿Qué pauta está sin embudo?",
    "¿Dónde se rompe mi marketing?",
  ],
  logistics: [
    "¿Dónde tengo más riesgo logístico?",
    "¿Qué pedidos deberían revisarse hoy?",
    "¿Qué significa mi tasa de entrega?",
  ],
  reports: [
    "¿Qué debería incluir mi reporte diario?",
    "¿Qué alertas deberían llegar por WhatsApp?",
    "¿Cómo resumirías el negocio para dirección?",
  ],
};

export function ChatSidebar() {
  const { isOpen, close, context, initialMessage } = useChatState();
  const {
    messages,
    conversations,
    conversationId,
    sendMessage,
    startNewConversation,
    selectConversation,
    isLoading,
    isLoadingHistory,
    error,
  } = useChatService(context);
  const [input, setInput] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initialSentRef = useRef(false);

  useEffect(() => {
    if (initialMessage && !initialSentRef.current && isOpen) {
      initialSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [initialMessage, isOpen, sendMessage]);

  useEffect(() => {
    if (!isOpen) initialSentRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) close();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, close]);

  const handleSubmit = () => {
    if (!input.trim() || isLoading) return;
    sendMessage(input);
    setInput("");
  };

  const suggestions =
    context.surface === "funnel"
      ? SUGGESTED_QUESTIONS_FUNNEL
      : context.surface === "funnels"
      ? SUGGESTED_QUESTIONS_FUNNELS
      : SUGGESTED_BY_SURFACE[context.surface as keyof typeof SUGGESTED_BY_SURFACE] ?? SUGGESTED_QUESTIONS_COMPANY;
  const subtitle =
    context.surface === "funnel" && context.funnel
      ? `Embudo ${context.funnel.funnelName} - ${context.funnel.productName}`
      : {
          funnel: "Vista de embudo",
          funnels: "Vista de embudos",
          company: "Vista de empresa",
          channels: "Canales del negocio",
          ads: "Vista de marketing",
          logistics: "Operación logística",
          reports: "Diseño de reportes",
        }[context.surface] ?? "Vista Finance";

  if (!isOpen) return null;

  return (
    <aside className="fixed top-0 right-0 z-40 flex h-screen w-[430px] flex-col overflow-hidden border-l border-gray-200 bg-white animate-in fade-in slide-in-from-right-4 duration-200">
      <header className="sticky top-0 z-10 flex shrink-0 items-start justify-between border-b border-blue-100 bg-white px-5 py-4 text-gray-900">
        <div>
          <h2
            className="inline-block bg-gradient-to-r from-[#214ec7] via-[#2b5ce6] to-[#67c6b3] bg-[length:200%_100%] bg-clip-text text-base font-extrabold text-transparent"
            style={{ animation: "gradientShift 5s ease infinite" }}
          >
            Fluxi Finance
          </h2>
          <p className="text-[11px] text-gray-500 mt-0.5">{subtitle}</p>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowHistory((value) => !value)}
            className="p-1.5 hover:bg-blue-50 rounded text-gray-400 hover:text-[#2b5ce6]"
            title="Últimos 20 chats"
            type="button"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={startNewConversation}
            className="p-1.5 hover:bg-blue-50 rounded text-gray-400 hover:text-[#2b5ce6]"
            title="Nuevo chat"
            type="button"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button onClick={close} className="p-1.5 hover:bg-blue-50 rounded text-gray-400 hover:text-[#2b5ce6]" title="Cerrar" type="button">
            <X className="w-4 h-4" />
          </button>
        </div>
      </header>

      {showHistory ? (
        <div className="shrink-0 border-b border-gray-100 bg-white px-5 py-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Últimos 20 chats de Finance
            </p>
            {isLoadingHistory ? <span className="text-[10px] text-gray-400">Cargando...</span> : null}
          </div>
          <div className="max-h-44 space-y-1 overflow-y-auto pr-1">
            {conversations.length === 0 ? (
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
                Aún no tienes chats guardados.
              </p>
            ) : (
              conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  onClick={() => {
                    selectConversation(conversation.id);
                    setShowHistory(false);
                  }}
                  className={`w-full rounded-lg px-3 py-2 text-left text-xs transition ${
                    conversation.id === conversationId
                      ? "bg-blue-50 text-[#2b5ce6]"
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                  type="button"
                >
                  <span className="block truncate font-semibold">{conversation.title}</span>
                  <span className="mt-0.5 block text-[10px] text-gray-400">{conversation.surface}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}

      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto bg-[#F7F8FB] px-5 py-5">
        {messages.length === 0 && !isLoading && (
          <div>
            <p className="mb-4 rounded-lg border border-blue-100 bg-white px-4 py-3 text-sm leading-6 text-gray-600 shadow-sm">
              Puedo leer datos reales de Finance para responder sobre empresa, embudos, marketing, logística y reportes.
              Solo verás tus últimos 20 chats aquí.
            </p>
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Preguntas sugeridas</p>
              {suggestions.map((question) => (
                <button
                  key={question}
                  onClick={() => sendMessage(question)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:bg-gray-100"
                  type="button"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <ChatMessage key={`${message.role}-${index}-${message.content.slice(0, 12)}`} message={message} />
        ))}

        {isLoading && (
          <div className="flex justify-start mb-3">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3.5 py-3">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "200ms" }} />
                <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse" style={{ animationDelay: "400ms" }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-100 text-red-700 text-xs rounded-lg px-3 py-2 mb-3">
            {error}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 shrink-0 border-t border-gray-100 bg-white p-4">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Escribe tu pregunta..."
            rows={1}
            disabled={isLoading}
            className="max-h-[120px] flex-1 resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none transition focus:border-[#2b5ce6] focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
          />
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isLoading}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#2b5ce6] text-white shadow-sm transition hover:bg-[#214ec7] disabled:cursor-not-allowed disabled:bg-gray-300"
            type="button"
            aria-label="Enviar mensaje"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
